import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { startIndeterminate, stopIndeterminate } from "./lib/routeProgress";
import { reportProgress, resetProgress } from "./lib/uploadProgress";
import { initPush } from "./lib/push";
import { getCountry } from "./country";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Veri ikametgahı: backend, fotoğrafı hangi ülkenin R2 bucket'ına yazacağını
// X-Country'den öğreniyor. X-Lang ise yapay zekâ çıktılarının (fotoğraf ret
// gerekçesi, acil durum tavsiyesi, asistan cevabı) hangi dilde üretileceğini
// belirler. İkisi de her istekte gitmeli.
const countryHeader = () => {
  const headers = { "X-Country": getCountry() };
  try {
    const lang = localStorage.getItem("lang");
    if (lang) headers["X-Lang"] = lang;
  } catch (e) {
    // Depolama kapalıysa başlığı atlıyoruz; backend Accept-Language'a düşer.
  }
  return headers;
};

const AuthContext = createContext({
  user: null,
  token: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  loading: true,
});

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { ...countryHeader(), Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (err) {
        // Only log the user out on a genuine auth rejection (token invalid or
        // expired). A network blip or a backend hiccup (5xx / timeout) must NOT
        // wipe the token — otherwise a transient error silently signs people out
        // and bounces them to /login on the next protected action (e.g. Kedi ekle).
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setToken(null);
          localStorage.removeItem("token");
        }
        // else: keep the token; user stays null this load but a refresh recovers.
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  // Register this device for push notifications once the user is signed in.
  // Silent no-op on web or until the native @capacitor/push-notifications
  // plugin is installed — never blocks anything.
  useEffect(() => {
    if (user?.id) { initPush(); }
  }, [user?.id]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { headers: countryHeader() });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const refreshUser = async () => {
    const t = localStorage.getItem("token");
    if (!t) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { ...countryHeader(), Authorization: `Bearer ${t}` } });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const register = async (payload) => {
    const res = await axios.post(`${API}/auth/register`, payload, { headers: countryHeader() });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  // Sign in with Google. Same call for a brand-new user and a returning one —
  // the backend creates the account if it has never seen this address, so there
  // is no "register vs log in" fork to make someone choose between while a hurt
  // cat waits. `idToken` comes from Google, and only Google can mint one.
  const loginWithGoogle = async (idToken) => {
    const res = await axios.post(`${API}/auth/google`, { id_token: idToken }, { headers: countryHeader() });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    // Reset one-time onboarding milestones so a new user on the same
    // browser sees them fresh.
    try {
      for (const k of [
        "firstCatMilestone:v1",
        "milestone5Cats:v1",
        "milestone10Cats:v1",
        "milestone25Cats:v1",
      ]) {
        localStorage.removeItem(k);
      }
    } catch { /* noop */ }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, token, login, loginWithGoogle, register, logout, loading, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const apiClient = () => {
  const token = localStorage.getItem("token");
  const client = axios.create({
    baseURL: API,
    // 90s ceiling: GPT-Vision matches take 5–20s typically; this keeps the
    // UI from "hanging" forever if the upstream is slow.
    timeout: 90000,
    headers: {
      ...countryHeader(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    // Real upload progress — fires for every POST/PUT with a body large
    // enough to matter (photos). reportProgress filters tiny JSON bodies.
    onUploadProgress: (e) => {
      if (!e || !e.total) return;
      reportProgress(e.loaded, e.total);
    },
  });
  // Drive the global top indeterminate progress bar for the duration of
  // every API call. Refcounted, so multiple concurrent requests collapse
  // into a single visible stripe.
  client.interceptors.request.use((config) => {
    startIndeterminate();
    return config;
  });
  const onDone = (v) => { stopIndeterminate(); return v; };
  const onErr = (e) => {
    stopIndeterminate();
    // If the request failed mid-upload, clear the bottom progress UI too.
    resetProgress();
    return Promise.reject(e);
  };
  client.interceptors.response.use(onDone, onErr);
  return client;
};
